import { select } from 'd3-selection';
import { scaleBand, scaleLinear } from 'd3-scale';
import { max, sum, extent } from 'd3-array';
import { axisBottom } from 'd3-axis';
import { line } from 'd3-shape';

import { convertWindBearing8 } from './convert-wind-bearing';
import { getRainColourInterpolator, getTemperatureColourInterpolator, getWindColourInterpolator } from './interpolators';
import { formatRainfall, formatWindGust } from './formatters';
import { getLowestMaxRainAxisValue } from './graph-limits';
import { convertRainfall } from './unit-converters';

class HourlyChart {

	constructor(weatherData, units) {

		this.naText = 'n/a';
		this.missingValue = null;
		this.units = units;

		this.data = weatherData.map(d => ({
			...d,
			key: d.time,
			hour: Number(d.timestamp.split(' ')[1].split(':')[0])
		}));

		this.keyFn = d => d.key;
		this.rainFormatter = rain => formatRainfall(rain, this.units.rain, true);

	}

	render(containerNode) {
		this._init(containerNode);
		this._renderVerticalGridlines();
		this._renderDaySeparator();
		this._renderSectionSeparators();
		this._renderRainfall();
		this._renderTemperature();
		this._renderWind();
		this._renderAxis();
		this._renderLabels();
	}

	_init(containerNode) {

		this.container = select(containerNode);
		const containerDim = this.container.node().getBoundingClientRect();

		this.dim = {
			svgWidth: containerDim.width,
			svgHeight: 420,
			marginTop: 15,
			marginRight: 10,
			marginBottom: 35,
			marginLeft: 10,
			sectionSpacing: 30,
			windSectionHeight: 70
		};

		// If available width is too small, certain things will need adjusting
		this.isSmall = this.dim.svgWidth < 840;

		// Rain section and temperature section height vary depending on how much space if left.
		// The rain section take up 40% of the remaining space, meaning the temperature section takes up 60%.
		const remaining = (this.dim.svgHeight - this.dim.marginTop - this.dim.marginBottom - this.dim.windSectionHeight - 2 * this.dim.sectionSpacing);
		this.dim.rainSectionHeight = 0.4 * remaining;
		this.dim.temperatureSectionHeight = 0.6 * remaining;

		// Width of the plot area is the svg width minus the left and right margins.
		this.dim.plotAreaWidth = this.dim.svgWidth - this.dim.marginLeft - this.dim.marginRight;

		// Height of the plot area is the svg height minus the top and bottom margins.
		this.dim.plotAreaHeight = this.dim.svgHeight - this.dim.marginTop - this.dim.marginBottom;

		this.elements = {};

		this.container.html('');

		// Insert the SVG element and apply the margins
		this.elements.svgRoot = this.container.append('svg')
			.attr('width', this.dim.svgWidth)
			.attr('height', this.dim.svgHeight);

		if (this.isSmall) {
			this.elements.svgRoot.attr('class', 'small');
		}

		this._createWindSymbolDefs();

		this.xScale = scaleBand()
			.domain(this.data.map(d => d.key))
			.rangeRound([0, this.dim.plotAreaWidth]);

		this.dim.pointWidth = this.xScale.step();

		// Get the SVG context we will be drawing in (simply applies the margins).
		this.elements.plotArea = this.elements.svgRoot.append('g')
			.attr('transform', `translate(${this.dim.marginLeft},${this.dim.marginTop})`);

	}

	_createWindSymbolDefs() {

		// Add a shape definition to the SVG. The shape we are defining is an arrow pointing north.
		// This represents a southerly wind direction. Insert this shape into the graphic via <use> elements,
		// and apply a rotate transformation to get the desired wind direction.

		const defs = this.elements.svgRoot
			.append('defs');

		this.dim.windSymbolSize = 52;

		// Radius of the background circle
		const windSymbolRadius = this.isSmall ? 12 : 14;

		const indicator = defs.append('g')
			.attr('id', 'wind-arrow');

		const y0 = 0.5 * this.dim.windSymbolSize - windSymbolRadius + 3;
		const length = 8;
		const y1 = y0 - length;
		const x0 = 0.5 * (this.dim.windSymbolSize - length * 1.2);
		const x1 = 0.5 * (this.dim.windSymbolSize + length * 1.2);

		indicator.append('path')
			.attr('d', `M${x0},${y1} L${x1},${y1} L${0.5 * this.dim.windSymbolSize},${y0} L${x0},${y1}`);

		const windSymbol = defs.append('g')
			.attr('id', 'wind-symbol');

		// Use circle shape
		windSymbol.append('circle')
			.attr('cx', 0.5 * this.dim.windSymbolSize)
			.attr('cy', 0.5 * this.dim.windSymbolSize)
			.attr('r', windSymbolRadius);

	}

	_renderVerticalGridlines() {

		const gridlines = this.elements.plotArea.append('g')
			.attr('class', 'background_lines');

		const drawLine = x => {
			gridlines.append('line')
				.attr('class', 'background_line')
				.attr('x1', x)
				.attr('x2', x)
				.attr('y1', this.dim.marginTop)
				.attr('y2', this.dim.plotAreaHeight);
		};

		this.data.forEach(d => {
			if ((d.hour - 1) % 3 === 0) {
				drawLine(this.xScale(d.key));
			}
		});

		const lastOb = this.data[this.data.length - 1];

		if (lastOb.hour % 3 === 0) {
			// Add extra background line at the end of the chart
			drawLine(this.xScale(lastOb.key) + this.dim.pointWidth);
		}

	}

	_renderDaySeparator() {

		// Insert day separator(s) between 12am the next time point. To do so, need to get
		// the positions of the two points and average them, then add half the interval width.
		// NOTE: This is not done for the case where 12am is the last x value.

		const n = this.data.length;
		const hours = this.data.map(d => d.hour);

		let indexOfMidnight = hours.indexOf(0);

		while (indexOfMidnight !== -1) {

			if (indexOfMidnight < n - 1) {

				const nextAfterMidnight = this.data[indexOfMidnight + 1].key;
				const midnightLineX = 0.5 * (this.xScale(this.data[indexOfMidnight].key) + this.xScale(nextAfterMidnight)) + 0.5 * this.dim.pointWidth;

				this.elements.plotArea.append('line')
					.attr('transform', `translate(${midnightLineX},0)`)
					.attr('class', 'vertical_separator')
					.attr('x1', 0)
					.attr('x2', 0)
					.attr('y1', this.dim.marginTop)
					.attr('y2', this.dim.plotAreaHeight);

			}

			indexOfMidnight = hours.indexOf(0, indexOfMidnight + 1);

		}

	}

	_renderSectionSeparators() {

		const yCoords = [

			// rain/temperature separator
			this.dim.plotAreaHeight - this.dim.rainSectionHeight - 0.5 * this.dim.sectionSpacing,

			// temperature/wind separator
			this.dim.windSectionHeight + 0.5 * this.dim.sectionSpacing

		];

		yCoords.forEach(y => {
			this.elements.plotArea.append('line')
				.attr('class', 'region_separator')
				.attr('transform', `translate(0,${y})`)
				.attr('x1', 0)
				.attr('x2', this.dim.plotAreaWidth)
				.attr('y1', 0)
				.attr('y2', 0);
		});

	}

	_renderRainfall() {

		// The rain scale. Leave space at top for labels.
		// Set a lowest maximum value for the scale.
		let lowestRainScaleMax = getLowestMaxRainAxisValue(this.units.rain);

		const rainScale = scaleLinear()
			.range([0, this.dim.rainSectionHeight - 30])
			.domain([0, Math.max(lowestRainScaleMax, max(this.data.map(d => d.hourlyRainfall)))])
			.clamp(true);

		const rainSection = this.elements.plotArea.append('g')
			.attr('class', 'rain_section');

		const getRainBarColour = getRainColourInterpolator(convertRainfall(15, this.units.rain));

		const validNonZeroData = this.data.filter(d => d.hourlyRainfall !== this.missingValue && d.hourlyRainfall > 0);

		// Create rainfall bars for dates with a positive rainfall value.
		rainSection.selectAll('rain-bar')
			.data(validNonZeroData, this.keyFn)
			.enter()
			.append('rect')
			.attr('class', 'rain_bar')
			.style('fill', d => getRainBarColour(d.hourlyRainfall, this.units.rain))
			.attr('x', d => this.xScale(d.key) + 0.15 * this.dim.pointWidth)
			.attr('y', d => this.dim.plotAreaHeight - rainScale(d.hourlyRainfall))
			.attr('width', 0.7 * this.dim.pointWidth)
			.attr('height', d => rainScale(d.hourlyRainfall));

		// Insert the bar labels. For zero rainfall, insert a small dot instead, and increase its font size.
		rainSection.selectAll('rain-label')
			.data(this.data, this.keyFn)
			.enter()
			.append('text')
			.attr('class', 'rain_label')
			.attr('text-anchor', 'middle')
			.attr('x', d => this.xScale(d.key) + 0.5 * this.dim.pointWidth)
			.attr('y', d => {
				const r = d.hourlyRainfall === this.missingValue ? 0 : d.hourlyRainfall;
				return this.dim.plotAreaHeight - rainScale(r) - 8;
			})
			.style('font-size', d => d.hourlyRainfall === 0 ? '15px' : '')
			.text(d => {
				if (d.hourlyRainfall > 0) {
					return formatRainfall(d.hourlyRainfall, this.units.rain, true);
				}
				return d.hourlyRainfall === 0 ? '.' : this.naText;
			});

	}

	_renderTemperature() {

		// The temperature scale
		const tempScale = scaleLinear()
			.range([this.dim.plotAreaHeight - this.dim.rainSectionHeight - this.dim.sectionSpacing,
				this.dim.windSectionHeight + this.dim.sectionSpacing]);

		// Calculate the range of the unadjusted data.
		const validTemps = this.data.map(d => d.temperature).filter(t => t !== this.missingValue);

		const tempScaleLimits = extent(validTemps);
		const tempScaleRange = tempScaleLimits[1] - tempScaleLimits[0];

		// Pad out the temperature axis limits, more so at the top.
		// If there is no variation throughout the period, use +/- 1 degree
		tempScaleLimits[0] = Math.floor(tempScaleLimits[0]) - Math.max(1, 0.15 * tempScaleRange);
		tempScaleLimits[1] = Math.ceil(tempScaleLimits[1]) + Math.max(1, 0.25 * tempScaleRange);

		// Set the domain of the temperature scale
		tempScale.domain(tempScaleLimits);

		// D3 line function for calculating temperature path coordinates
		const temperatureLine = line()
			.x(d => this.xScale(d.key) + 0.5 * this.dim.pointWidth)
			.y(d => tempScale(d.temperature))
			.defined(d => d.temperature !== this.missingValue);

		const getTemperatureColour = getTemperatureColourInterpolator(false, this.units.temperature);

		const tempSection = this.elements.plotArea.append('g')
			.attr('class', 'temperature_section');

		// Draw the temperature line
		tempSection.append('path')
			.attr('class', 'temperature_path')
			.attr('d', temperatureLine(this.data));

		// Draw the temperature labels
		tempSection.selectAll('temperature-label')
			.data(this.data, this.keyFn)
			.enter()
			.append('text')
			.attr('class', 'temperature_label')
			.attr('x', d => this.xScale(d.key) + 0.5 * this.dim.pointWidth)
			.attr('y', d => tempScale(d.temperature))
			.attr('dy', '0.35em')
			.attr('text-anchor', 'middle')
			.style('fill', d => d.temperature !== this.missingValue ? getTemperatureColour(d.temperature) : 'none')
			.text(d => d.temperature !== this.missingValue ? d.temperature : '');

	}

	_renderWind() {

		const isTooNarrowForArrows = this.dim.plotAreaWidth < 750;
		const getWindColour = getWindColourInterpolator(this.units.speed);

		const windSection = this.elements.plotArea.append('g')
			.attr('class', 'wind_section');

		const validGustData = this.data.filter(d => d.hourlyHighWindGust !== this.missingValue);
		const nonZeroGustBearingData = validGustData.filter(d => d.windBearing !== this.missingValue && d.hourlyHighWindGust > 0);

		// Draw the arrows with <use> elements. They need to be rotated according to the
		// wind direction at a particular point.
		windSection.append('g')
			.attr('class', 'wind_symbols')
			.selectAll('wind-symbol')
			.data(validGustData, this.keyFn)
			.enter()
			.append('use')
			.attr('xlink:href', '#wind-symbol')
			.attr('transform', d => `translate(${this.xScale(d.key) + 0.5 * this.dim.pointWidth - 0.5 * this.dim.windSymbolSize},35)`)
			.style('fill', d => getWindColour(d.hourlyHighWindGust, this.units.speed));

		if (!isTooNarrowForArrows) {
			windSection.append('g')
				.attr('class', 'wind_arrows')
				.selectAll('wind-indicator')
				.data(nonZeroGustBearingData, this.keyFn)
				.enter()
				.append('use')
				.attr('xlink:href', '#wind-arrow')
				.attr('transform', d => {
					const x = this.xScale(d.key) + 0.5 * this.dim.pointWidth;
					const rotate = 45 * Math.round(d.windBearing / 45);
					const rotateCenter = 0.5 * this.dim.windSymbolSize;
					return `translate(${x - rotateCenter},35) rotate(${rotate} ${rotateCenter} ${rotateCenter})`;
				});
		}

		// Draw the wind speed labels
		windSection.append('g')
			.attr('class', 'wind_speed_labels')
			.selectAll('wind-speed-label')
			.data(this.data, this.keyFn)
			.enter()
			.append('text')
			.attr('class', d => d.hourlyHighWindGust === this.missingValue ? 'na_label' : null)
			.attr('x', d => this.xScale(d.key) + 0.5 * this.dim.pointWidth)
			.attr('y', 65)
			.attr('text-anchor', 'middle')
			.text(d => d.hourlyHighWindGust === this.missingValue
				? this.naText
				: formatWindGust(d.hourlyHighWindGust, this.units.speed, true, true)
			);

		// Draw the wind direction labels, directly above the wind speed labels
		windSection.append('g')
			.attr('class', 'wind_direction_labels')
			.selectAll('wind-direction-label')
			.data(this.data, this.keyFn)
			.enter()
			.append('text')
			.attr('x', d => this.xScale(d.key) + 0.5 * this.dim.pointWidth)
			.attr('y', 35)
			.attr('text-anchor', 'middle')
			.text(d => d.windBearing === this.missingValue || d.hourlyHighWindGust === 0 ? '' : convertWindBearing8(d.windBearing));

	}

	_renderLabels() {

		const labels = [
			{
				y: this.dim.plotAreaHeight - this.dim.rainSectionHeight - 0.5 * this.dim.sectionSpacing,
				text: `Rainfall (${this.units.rain})`
			},
			{
				y: this.dim.windSectionHeight + 0.5 * this.dim.sectionSpacing,
				text: `Temperature (°${this.units.temperature})`
			},
			{
				y: -5,
				text: `Avg wind direction, Highest gust (${this.units.speed})`
			}
		];

		labels.forEach(label => {
			this.elements.plotArea.append('text')
				.attr('class', 'parameter_label')
				.attr('x', 2)
				.attr('y', label.y)
				.attr('dy', '1.3em')
				.text(label.text);
		});

		const validRainfalls = this.data.map(d => d.hourlyRainfall).filter(r => r !== this.missingValue);

		const total = validRainfalls.length === 0
			? 'n/a'
			: this.rainFormatter(sum(validRainfalls));

		// Draw rain total label at top-right of rain region
		// Remember to filter for NA values before summing!
		this.elements.plotArea.append('text')
			.attr('class', 'rain_total_label')
			.attr('x', this.dim.plotAreaWidth - 5)
			.attr('y', this.dim.plotAreaHeight - this.dim.rainSectionHeight - 0.5 * this.dim.sectionSpacing)
			.attr('text-anchor', 'end')
			.attr('dy', '1.3em')
			.text('Total: ' + total);

	}

	_renderAxis() {

		const xAxis = axisBottom()
			.scale(this.xScale)
			.tickSizeOuter(0)
			.tickSizeInner(0)
			.tickFormat((t, i) => {

				const timeString = this.data[i].timestamp;
				const timePart = timeString.split(' ')[1];

				if (timePart === '23:59:59') { return '12am'; }

				let hour = Number(timePart.split(':')[0]);

				if (hour % 2) { return ''; }
				if (hour > 12) { return (hour - 12) + 'pm'; }
				if (hour === 12) { return '12pm'; }

				if (hour === 0) { hour = 12; }

				return hour + 'am';
			});

		// Draw the x axis and labels
		this.elements.plotArea.append('g')
			.attr('class', 'axis')
			.attr('id', 'main-x-axis')
			.attr('transform', `translate(0,${this.dim.plotAreaHeight})`)
			.call(xAxis)
			.selectAll('text')
			.attr('dy', '1.4em');

	}

}

export default HourlyChart;



// WEBPACK FOOTER //
// ./utils/hourly-chart.js